/**
 * @file TelemetryService.js
 * @description Servicio de telemetría para monitoreo avanzado del sistema CORS y otros componentes
 * Implementa el patrón Observer para registrar eventos del sistema y estadísticas de rendimiento
 */

import diagnosticService from './DiagnosticService';

/**
 * Clase que define eventos de telemetría estándar
 * @readonly
 * @enum {string}
 */
export const TelemetryEvents = {
  PROXY_USED: 'proxy_used',
  PROXY_BYPASSED: 'proxy_bypassed',
  CORS_ERROR: 'cors_error',
  RETRY_SUCCESS: 'retry_success',
  RETRY_FAILURE: 'retry_failure',
  API_CALL_SUCCESS: 'api_call_success',
  API_CALL_FAILURE: 'api_call_failure',
  SERVER_STATUS_CHANGE: 'server_status_change'
};

/**
 * Servicio para recolección de telemetría y métricas avanzadas
 * Implementa el patrón Singleton y Observer
 */
class TelemetryService {
  constructor() {
    // Almacenamiento de eventos en memoria (para eventos actuales)
    this.events = [];
    
    // Límite de eventos en memoria
    this.maxEvents = 100;
    
    // Clave en localStorage para eventos persistentes
    this.storageKey = 'nubelytics_telemetry_events';
    
    // Observadores registrados
    this.observers = new Map();
    
    // Métricas agregadas
    this.metrics = {
      totalProxyRequests: 0,
      totalDirectRequests: 0,
      totalRetries: 0,
      totalErrors: 0,
      domainStats: new Map(),
      responseTimeMs: {
        sum: 0,
        count: 0,
        min: Number.MAX_SAFE_INTEGER,
        max: 0
      }
    };
    
    // Cargar métricas guardadas
    this.loadMetrics();
    
    // Intervalo para guardar métricas (cada 5 minutos)
    this.persistInterval = setInterval(() => this.persistMetrics(), 5 * 60 * 1000);
  }
  
  /**
   * Registra un evento de telemetría
   * @param {string} eventType - Tipo de evento (usar TelemetryEvents)
   * @param {Object} eventData - Datos adicionales del evento
   */
  trackEvent(eventType, eventData = {}) {
    const timestamp = new Date();
    const event = {
      type: eventType,
      timestamp: timestamp.toISOString(),
      data: { ...eventData }
    };
    
    // Añadir a memoria
    this.events.unshift(event);
    
    // Limitar tamaño
    if (this.events.length > this.maxEvents) {
      this.events.length = this.maxEvents;
    }
    
    // Actualizar métricas
    this.updateMetrics(eventType, eventData);
    
    // Notificar a observadores
    this.notifyObservers(event);
    
    // Integrar con diagnóstico
    diagnosticService.logSystemEvent(eventType, eventData);
    
    return event;
  }
  
  /**
   * Actualiza las métricas en base a un evento
   * @private
   * @param {string} eventType - Tipo de evento
   * @param {Object} eventData - Datos del evento
   */
  updateMetrics(eventType, eventData) {
    switch (eventType) {
      case TelemetryEvents.PROXY_USED:
        this.metrics.totalProxyRequests++;
        // Registrar estadísticas por dominio
        if (eventData.domain) {
          const domainStats = this.metrics.domainStats.get(eventData.domain) || {
            proxyCount: 0,
            directCount: 0,
            errorCount: 0
          };
          domainStats.proxyCount++;
          this.metrics.domainStats.set(eventData.domain, domainStats);
        }
        
        // Registrar tiempo de respuesta
        if (eventData.responseTimeMs) {
          const rt = this.metrics.responseTimeMs;
          rt.sum += eventData.responseTimeMs;
          rt.count++;
          rt.min = Math.min(rt.min, eventData.responseTimeMs);
          rt.max = Math.max(rt.max, eventData.responseTimeMs);
        }
        break;
        
      case TelemetryEvents.PROXY_BYPASSED:
        this.metrics.totalDirectRequests++;
        // Registrar estadísticas por dominio
        if (eventData.domain) {
          const domainStats = this.metrics.domainStats.get(eventData.domain) || {
            proxyCount: 0,
            directCount: 0,
            errorCount: 0
          };
          domainStats.directCount++;
          this.metrics.domainStats.set(eventData.domain, domainStats);
        }
        break;
        
      case TelemetryEvents.CORS_ERROR:
      case TelemetryEvents.API_CALL_FAILURE:
        this.metrics.totalErrors++;
        // Registrar estadísticas por dominio
        if (eventData.domain) {
          const domainStats = this.metrics.domainStats.get(eventData.domain) || {
            proxyCount: 0,
            directCount: 0,
            errorCount: 0
          };
          domainStats.errorCount++;
          this.metrics.domainStats.set(eventData.domain, domainStats);
        }
        break;
        
      case TelemetryEvents.RETRY_SUCCESS:
      case TelemetryEvents.RETRY_FAILURE:
        this.metrics.totalRetries++;
        break;
    }
  }
  
  /**
   * Obtiene un resumen de las métricas actuales
   * @returns {Object} Métricas de telemetría
   */
  getMetricsSummary() {
    const domainStatsObject = {};
    
    // Convertir Map a objeto para facilitar serialización
    this.metrics.domainStats.forEach((value, key) => {
      domainStatsObject[key] = { ...value };
    });
    
    // Calcular promedio de tiempo de respuesta
    const avgResponseTime = this.metrics.responseTimeMs.count > 0 
      ? this.metrics.responseTimeMs.sum / this.metrics.responseTimeMs.count 
      : 0;
    
    return {
      totalRequests: this.metrics.totalProxyRequests + this.metrics.totalDirectRequests,
      totalProxyRequests: this.metrics.totalProxyRequests,
      totalDirectRequests: this.metrics.totalDirectRequests,
      totalRetries: this.metrics.totalRetries,
      totalErrors: this.metrics.totalErrors,
      responseTime: {
        avgMs: avgResponseTime,
        minMs: this.metrics.responseTimeMs.min === Number.MAX_SAFE_INTEGER ? 0 : this.metrics.responseTimeMs.min,
        maxMs: this.metrics.responseTimeMs.max,
      },
      domainStats: domainStatsObject,
      timestamp: new Date().toISOString(),
      collectionDuration: `${Math.round((Date.now() - this.metricsStartTime)/1000/60)} minutos`,
    };
  }
  
  /**
   * Guarda las métricas actuales en localStorage
   * @private
   */
  persistMetrics() {
    try {
      const summary = this.getMetricsSummary();
      localStorage.setItem('nubelytics_telemetry_metrics', JSON.stringify(summary));
      
      // Guardar solo los últimos 50 eventos para no sobrecargar localStorage
      const eventsToStore = this.events.slice(0, 50);
      localStorage.setItem(this.storageKey, JSON.stringify(eventsToStore));
      
      console.log('[TelemetryService] Métricas guardadas correctamente');
    } catch (error) {
      console.error('[TelemetryService] Error al persistir métricas:', error);
    }
  }
  
  /**
   * Carga métricas guardadas desde localStorage
   * @private
   */
  loadMetrics() {
    try {
      // Inicializar tiempo de inicio de recolección de métricas
      this.metricsStartTime = Date.now();
      
      // Cargar eventos previos
      const storedEvents = localStorage.getItem(this.storageKey);
      if (storedEvents) {
        this.events = JSON.parse(storedEvents);
      }
      
      // Cargar métricas previas (para continuidad entre sesiones)
      const storedMetrics = localStorage.getItem('nubelytics_telemetry_metrics');
      if (storedMetrics) {
        const metrics = JSON.parse(storedMetrics);
        
        // Restaurar valores simples
        this.metrics.totalProxyRequests = metrics.totalProxyRequests || 0;
        this.metrics.totalDirectRequests = metrics.totalDirectRequests || 0;
        this.metrics.totalRetries = metrics.totalRetries || 0;
        this.metrics.totalErrors = metrics.totalErrors || 0;
        
        // Restaurar estadísticas por dominio
        if (metrics.domainStats) {
          Object.entries(metrics.domainStats).forEach(([domain, stats]) => {
            this.metrics.domainStats.set(domain, { ...stats });
          });
        }
      }
      
      console.log('[TelemetryService] Métricas cargadas correctamente');
    } catch (error) {
      console.error('[TelemetryService] Error al cargar métricas:', error);
      // Reiniciar métricas en caso de error
      this.resetMetrics();
    }
  }
  
  /**
   * Reinicia todas las métricas
   */
  resetMetrics() {
    this.metricsStartTime = Date.now();
    this.events = [];
    this.metrics = {
      totalProxyRequests: 0,
      totalDirectRequests: 0,
      totalRetries: 0,
      totalErrors: 0,
      domainStats: new Map(),
      responseTimeMs: {
        sum: 0,
        count: 0,
        min: Number.MAX_SAFE_INTEGER,
        max: 0
      }
    };
    
    localStorage.removeItem('nubelytics_telemetry_metrics');
    localStorage.removeItem(this.storageKey);
    
    console.log('[TelemetryService] Métricas reiniciadas');
  }
  
  /**
   * Registra un observador para eventos de telemetría
   * @param {string} name - Identificador único del observador
   * @param {Function} callback - Función a llamar cuando ocurra un evento
   * @returns {boolean} Éxito del registro
   */
  registerObserver(name, callback) {
    if (typeof callback !== 'function') {
      console.error('[TelemetryService] El observer debe ser una función');
      return false;
    }
    
    this.observers.set(name, callback);
    return true;
  }
  
  /**
   * Elimina un observador registrado
   * @param {string} name - Identificador del observador
   * @returns {boolean} Éxito de la eliminación
   */
  unregisterObserver(name) {
    return this.observers.delete(name);
  }
  
  /**
   * Notifica a todos los observadores registrados
   * @private
   * @param {Object} event - Evento de telemetría
   */
  notifyObservers(event) {
    this.observers.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('[TelemetryService] Error en observer:', error);
      }
    });
  }
  
  /**
   * Limpia recursos cuando el servicio ya no se necesita
   */
  destroy() {
    if (this.persistInterval) {
      clearInterval(this.persistInterval);
    }
    this.persistMetrics();
    this.observers.clear();
  }
}

// Exportar como singleton
const telemetryService = new TelemetryService();
export default telemetryService;
